import { Box, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { lazy, Suspense, useState } from 'react';
import { useParams } from 'react-router';
import PageLoader from 'src/UI/loader/PageLoader';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { showError, showSuccess } from 'src/notifications/notifications';
import { expenditureColumns, incomeColumns, lifeEventColumns } from './cashflowColumns';
import { toEditableExpenditure, toEditableIncome, toEditableLifeEvent } from './cashflowMappers';
import CashflowContentDeprecated from './CashflowContentDeprecated';
import CashflowSection from './components/CashflowSection';
import CashflowTable from './components/CashflowTable';
import { SectionTotal } from './components/cashflowCells';
import { ExpenditureModal } from './form/ExpenditureModal';
import { IncomeModal } from './form/IncomeModal';
import { LifeEventModal } from './form/LifeEventModal';
import useExpenditures, { EditableExpenditure, Expenditure } from './hooks/useExpenditures';
import useIncomes, { EditableIncome, Income } from './hooks/useIncomes';
import useLifeEvents, { LifeEvent } from './hooks/useLifeEvents';
import { CashflowModelsSection } from './modelling/CashflowModelsSection';
import { formatCurrency, Frequency, useTotalForFrequency } from './utils';

// Lazy so echarts ships in an async chunk only modelling users download.
const ModelDetailContent = lazy(() => import('./modelling/ModelDetailContent'));

export default function CashflowContent() {
  const { flagEnabledFamilyCashflowsRevamp, flagEnabledCashflowModelling } = useFlags();
  const { sectionSlug, selectedEventId } = useParams();

  if (flagEnabledCashflowModelling && sectionSlug === 'model' && selectedEventId) {
    return (
      <Suspense fallback={<PageLoader absolute={false} />}>
        <ModelDetailContent scenarioId={selectedEventId} />
      </Suspense>
    );
  }

  return (
    <>
      {flagEnabledFamilyCashflowsRevamp ? (
        <CashflowContentInternal />
      ) : (
        <CashflowContentDeprecated />
      )}
      {flagEnabledCashflowModelling && (
        <Box mt="xl" pb="xl">
          <CashflowModelsSection />
        </Box>
      )}
    </>
  );
}

function CashflowContentInternal() {
  const { isLoading: loadingIncomes, incomesData, upsertIncomes, deleteIncomes } = useIncomes();
  const {
    isLoading: loadingExpenditures,
    expendituresData,
    upsertExpenditures,
    deleteExpenditures,
  } = useExpenditures();
  const { lifeEventsData, upsertLifeEvents, deleteLifeEvents } = useLifeEvents();

  const [incomeFrequency, setIncomeFrequency] = useState<Frequency>('monthly');
  const [expenditureFrequency, setExpenditureFrequency] = useState<Frequency>('monthly');

  const [incomeModalOpened, { open: openIncomeModal, close: closeIncomeModal }] =
    useDisclosure(false);
  const [selectedIncome, setSelectedIncome] = useState<EditableIncome | undefined>();

  const [expenditureModalOpened, { open: openExpenditureModal, close: closeExpenditureModal }] =
    useDisclosure(false);
  const [selectedExpenditure, setSelectedExpenditure] = useState<EditableExpenditure | undefined>();

  const [lifeEventModalOpened, { open: openLifeEventModal, close: closeLifeEventModal }] =
    useDisclosure(false);
  const [selectedLifeEvent, setSelectedLifeEvent] = useState<LifeEvent | undefined>();

  const incomeMonthlyPreTax = useTotalForFrequency({
    items: incomesData,
    frequency: 'monthly',
    frequencyField: 'frequency',
    amountField: 'amountPreTax',
  });
  const incomeMonthlyPostTax = useTotalForFrequency({
    items: incomesData,
    frequency: 'monthly',
    frequencyField: 'frequency',
    amountField: 'amountPostTax',
  });
  const incomeAnnualPreTax = useTotalForFrequency({
    items: incomesData,
    frequency: 'annually',
    frequencyField: 'frequency',
    amountField: 'amountPreTax',
  });
  const incomeAnnualPostTax = useTotalForFrequency({
    items: incomesData,
    frequency: 'annually',
    frequencyField: 'frequency',
    amountField: 'amountPostTax',
  });

  if (loadingIncomes || loadingExpenditures) {
    return <PageLoader absolute={false} />;
  }

  const incomes = incomesData ?? [];
  const expenditures = expendituresData ?? [];
  const lifeEvents = lifeEventsData ?? [];

  const incomeIsMonthly = incomeFrequency === 'monthly';
  const incomeTotalPreTax = incomeIsMonthly ? incomeMonthlyPreTax : incomeAnnualPreTax;
  const incomeTotalPostTax = incomeIsMonthly ? incomeMonthlyPostTax : incomeAnnualPostTax;
  const incomeTotalTooltip = incomeIsMonthly
    ? `Annually: +${formatCurrency(incomeAnnualPreTax)} (${formatCurrency(incomeAnnualPostTax)} after tax)`
    : `Monthly: +${formatCurrency(incomeMonthlyPreTax)} (${formatCurrency(incomeMonthlyPostTax)} after tax)`;

  const expenditureAmountKey =
    expenditureFrequency === 'monthly' ? 'amountMonthly' : 'amountAnnually';
  const expenditureTotal = expenditures.reduce((sum, item) => sum + item[expenditureAmountKey], 0);
  const monthlyExpenditure = expenditures.reduce((sum, item) => sum + item.amountMonthly, 0);
  const annualExpenditure = expenditures.reduce((sum, item) => sum + item.amountAnnually, 0);
  const expenditureTotalTooltip =
    expenditureFrequency === 'monthly'
      ? `Annually: -${formatCurrency(annualExpenditure)}`
      : `Monthly: -${formatCurrency(monthlyExpenditure)}`;

  const handleSaveIncome = async (income: EditableIncome) => {
    try {
      await upsertIncomes([income]);
      showSuccess('Income saved successfully');
      closeIncomeModal();
      setSelectedIncome(undefined);
    } catch {
      showError('Failed to save income');
    }
  };

  const handleSaveExpenditure = async (
    expenditure: Omit<EditableExpenditure, 'id' | 'clientId'>,
  ) => {
    const item = { ...expenditure, id: selectedExpenditure?.id };
    try {
      await upsertExpenditures([item]);
      showSuccess('Expenditure saved successfully');
      closeExpenditureModal();
      setSelectedExpenditure(undefined);
    } catch {
      showError('Failed to save expenditure');
    }
  };

  const handleSaveLifeEvent = async (lifeEvent: Omit<LifeEvent, 'id' | 'clientId'>) => {
    const item = { ...lifeEvent, id: selectedLifeEvent?.id };
    try {
      await upsertLifeEvents([item]);
      showSuccess('Life event saved successfully');
      closeLifeEventModal();
      setSelectedLifeEvent(undefined);
    } catch {
      showError('Failed to save life event');
    }
  };

  return (
    <Stack gap="md">
      <CashflowSection
        value="incomes"
        title="Income"
        count={incomes.length}
        frequency={incomeFrequency}
        onFrequencyChange={setIncomeFrequency}
        showFrequencyToggle
        onAdd={() => {
          setSelectedIncome(undefined);
          openIncomeModal();
        }}
        totalNode={
          <SectionTotal tooltipLabel={incomeTotalTooltip}>
            +{formatCurrency(incomeTotalPreTax)} ({formatCurrency(incomeTotalPostTax)} after tax)
          </SectionTotal>
        }
      >
        <CashflowTable<Income>
          items={incomes}
          columns={incomeColumns(incomeFrequency)}
          onEdit={income => {
            setSelectedIncome(toEditableIncome(income));
            openIncomeModal();
          }}
          onDelete={income => income.id && deleteIncomes([income.id])}
        />
      </CashflowSection>

      <CashflowSection
        value="expenditures"
        title="Expenditure"
        count={expenditures.length}
        frequency={expenditureFrequency}
        onFrequencyChange={setExpenditureFrequency}
        showFrequencyToggle
        onAdd={() => {
          setSelectedExpenditure(undefined);
          openExpenditureModal();
        }}
        totalNode={
          <SectionTotal tooltipLabel={expenditureTotalTooltip}>
            -{formatCurrency(expenditureTotal)}
          </SectionTotal>
        }
      >
        <CashflowTable<Expenditure>
          items={expenditures}
          columns={expenditureColumns(expenditureFrequency)}
          onEdit={expenditure => {
            setSelectedExpenditure(toEditableExpenditure(expenditure));
            openExpenditureModal();
          }}
          onDelete={expenditure => expenditure.id && deleteExpenditures([expenditure.id])}
        />
      </CashflowSection>

      <CashflowSection
        value="lifeEvents"
        title="Future life events"
        count={lifeEvents.length}
        onAdd={() => {
          setSelectedLifeEvent(undefined);
          openLifeEventModal();
        }}
        totalNode={
          <SectionTotal>
            -{formatCurrency(lifeEvents.reduce((sum, item) => sum + (item.cost || 0), 0))}
          </SectionTotal>
        }
      >
        <CashflowTable<LifeEvent>
          items={lifeEvents}
          columns={lifeEventColumns}
          onEdit={lifeEvent => {
            setSelectedLifeEvent(toEditableLifeEvent(lifeEvent));
            openLifeEventModal();
          }}
          onDelete={lifeEvent => lifeEvent.id && deleteLifeEvents([lifeEvent.id])}
        />
      </CashflowSection>

      <IncomeModal
        opened={incomeModalOpened}
        onClose={closeIncomeModal}
        onSubmit={handleSaveIncome}
        initialValues={selectedIncome}
      />
      <ExpenditureModal
        opened={expenditureModalOpened}
        onClose={closeExpenditureModal}
        onSubmit={handleSaveExpenditure}
        initialValues={selectedExpenditure}
      />
      <LifeEventModal
        opened={lifeEventModalOpened}
        onClose={closeLifeEventModal}
        onSubmit={handleSaveLifeEvent}
        initialValues={selectedLifeEvent}
      />
    </Stack>
  );
}
